import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel
} from '@mui/material';
import { Add as AddIcon, Person, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { usersAPI, hospitalsAPI } from '../../services/api';
import toast from 'react-hot-toast';

function Users() {
  const { tenantId, isSuperAdmin, userRole } = useAuth();
  const [users, setUsers] = useState([]);
  const [hospitals, setHospitals] = useState([]);
  const [selectedTenant, setSelectedTenant] = useState(tenantId);
  const [open, setOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    role: 'technician',
    active: true,
    tenant_id: tenantId
  });

  const loadUsers = useCallback(async () => {
    try {
      const params = isSuperAdmin && selectedTenant ? { tenant_id: selectedTenant } : {};
      const response = await usersAPI.getAll(params);
      const usersData = Array.isArray(response?.data?.data) ? response.data.data : 
                       Array.isArray(response?.data) ? response.data : [];
      setUsers(usersData);
    } catch (error) {
      console.error('Failed to load users:', error.message);
      toast.error('Erro ao carregar usuários');
      setUsers([]);
    }
  }, [selectedTenant, isSuperAdmin]);
  
  const loadHospitals = useCallback(async () => {
    if (!isSuperAdmin) return;
    
    try {
      const response = await hospitalsAPI.getAll();
      const hospitalsData = Array.isArray(response?.data?.data) ? response.data.data : 
                           Array.isArray(response?.data) ? response.data : [];
      setHospitals(hospitalsData);
    } catch (error) {
      console.warn('Failed to load hospitals:', error.message);
    }
  }, [isSuperAdmin]);

  useEffect(() => {
    loadUsers();
    loadHospitals();
  }, [loadUsers, loadHospitals]);
  
  useEffect(() => {
    if (isSuperAdmin && selectedTenant !== tenantId) {
      loadUsers();
    }
  }, [selectedTenant, isSuperAdmin, tenantId, loadUsers]);

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      name: user.name,
      email: user.email,
      role: user.role,
      active: user.active
    });
    setOpen(true);
  };

  const handleDelete = async (userId) => {
    if (window.confirm('Tem certeza que deseja excluir este usuário?')) {
      try {
        await usersAPI.delete(userId);
        toast.success('Usuário excluído com sucesso!');
        // Recarrega a lista para garantir sincronização
        await loadUsers();
      } catch (error) {
        toast.error(error.response?.data?.message || 'Erro ao excluir usuário');
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingUser) {
        await usersAPI.update(editingUser.id, formData);
        toast.success('Usuário atualizado com sucesso!');
      } else {
        const targetTenant = isSuperAdmin ? (formData.tenant_id || selectedTenant) : tenantId;
        const userData = { ...formData, tenant_id: targetTenant };
        const response = await usersAPI.create(userData);
        
        if (response.data.data.tempPassword) {
          toast.success(`Usuário criado! Senha temporária: ${response.data.data.tempPassword}`);
        } else {
          toast.success('Usuário criado com sucesso!');
        }
      }
      handleCloseDialog();
      // Recarrega a lista para garantir sincronização
      await loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao salvar usuário');
    }
  };

  const handleCloseDialog = () => {
    setOpen(false);
    setEditingUser(null);
    setFormData({
      name: '',
      email: '',
      role: 'technician',
      active: true,
      tenant_id: isSuperAdmin ? selectedTenant : tenantId
    });
  };

  const handleChange = (e) => {
    const { name, value, checked } = e.target;
    setFormData({
      ...formData,
      [name]: name === 'active' ? checked : value
    });
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'super_admin': return 'secondary';
      case 'admin': return 'error';
      case 'manager': return 'warning';
      case 'technician': return 'primary';
      default: return 'default';
    }
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case 'super_admin': return 'Super Admin';
      case 'admin': return 'Administrador';
      case 'manager': return 'Gerente';
      case 'technician': return 'Técnico';
      default: return role;
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">
          Usuários {isSuperAdmin && '(Super Admin)'}
        </Typography>
        <Box display="flex" gap={2} alignItems="center">
          {isSuperAdmin && (
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel>Hospital/Tenant</InputLabel>
              <Select
                value={selectedTenant || ''}
                onChange={(e) => setSelectedTenant(e.target.value)}
                label="Hospital/Tenant"
              >
                <MenuItem key="all" value="">Todos os Hospitais</MenuItem>
                {hospitals.map((hospital) => (
                  <MenuItem key={hospital.id} value={hospital.id}>
                    {hospital.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {['super_admin', 'admin'].includes(userRole) && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setOpen(true)}
            >
              Novo Usuário
            </Button>
          )}
        </Box>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Nome</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Função</TableCell>
              {isSuperAdmin && <TableCell>Hospital</TableCell>}
              <TableCell>Status</TableCell>
              <TableCell>Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {Array.isArray(users) && users.map((user) => (
              <TableRow key={user.id}>
                <TableCell>
                  <Box display="flex" alignItems="center">
                    <Person sx={{ mr: 1 }} />
                    {user.name}
                  </Box>
                </TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell>
                  <Chip
                    label={getRoleLabel(user.role)}
                    color={getRoleColor(user.role)}
                    size="small"
                  />
                </TableCell>
                {isSuperAdmin && (
                  <TableCell>
                    {user.tenant_id ? (
                      hospitals.find(h => h.id === user.tenant_id)?.name || user.tenant_id
                    ) : (
                      <Chip label="Global" size="small" color="secondary" />
                    )}
                  </TableCell>
                )}
                <TableCell>
                  <Chip
                    label={user.active ? 'Ativo' : 'Inativo'}
                    color={user.active ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  {['super_admin', 'admin'].includes(userRole) && (
                    <>
                      <Button
                        size="small"
                        startIcon={<EditIcon />}
                        onClick={() => handleEdit(user)}
                        sx={{ mr: 1 }}
                      >
                        Editar
                      </Button>
                      <Button
                        size="small"
                        color="error"
                        startIcon={<DeleteIcon />}
                        onClick={() => handleDelete(user.id)}
                      >
                        Excluir
                      </Button>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {['super_admin', 'admin'].includes(userRole) && (
        <Dialog open={open} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingUser ? 'Editar Usuário' : 'Novo Usuário'}</DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  name="name"
                  label="Nome Completo"
                  fullWidth
                  required
                  value={formData.name}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  name="email"
                  label="Email"
                  type="email"
                  fullWidth
                  required
                  value={formData.email}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Função</InputLabel>
                  <Select
                    name="role"
                    value={formData.role}
                    onChange={handleChange}
                  >
                    {isSuperAdmin && <MenuItem key="super_admin" value="super_admin">Super Administrador</MenuItem>}
                    <MenuItem key="admin" value="admin">Administrador</MenuItem>
                    <MenuItem key="manager" value="manager">Gerente</MenuItem>
                    <MenuItem key="technician" value="technician">Técnico</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              {isSuperAdmin && (
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Hospital</InputLabel>
                    <Select
                      name="tenant_id"
                      value={formData.tenant_id || ''}
                      onChange={handleChange}
                      disabled={formData.role === 'super_admin'}
                    >
                      <MenuItem key="global" value="">Nenhum (Global)</MenuItem>
                      {hospitals.map((hospital) => (
                        <MenuItem key={hospital.id} value={hospital.id}>
                          {hospital.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      name="active"
                      checked={formData.active}
                      onChange={handleChange}
                    />
                  }
                  label="Usuário Ativo"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancelar</Button>
            <Button type="submit" variant="contained">
              {editingUser ? 'Atualizar' : 'Salvar'}
            </Button>
          </DialogActions>
        </form>
        </Dialog>
      )}
    </Box>
  );
}

export default Users;